import React, { useEffect, useState } from "react";

// third-party imports
import {
  RiHeartLine,
  RiHeartFill,
  RiShareForwardLine,
  RiThumbUpLine,
  RiThumbUpFill,
  RiThumbDownLine,
  RiThumbDownFill,
} from "react-icons/ri";

// project imports
import { GoogleAnalyticsEnum, triggerFunction } from "../../utils/google-analytics";
import { useQuickInsightContext } from "../../context/quick_insights_context";
import Spinner from "../../components/spinner-component/SpinnerOne";
import { useUserContext } from "../../context/user_context";
import { countReactions, getOrCreateGuestId } from "../../utils";
import ToolTip from "../../common/Tooltip/ToolTip";

function InteractionComponent({
  onShare,
  selectedArticle,
  setIsOpen,
  openModalLogin
}) {
  // Reading Variables from context
  const { isInteractionLoading, insightsUserInteraction, upsertInsightsUserInteraction } = useQuickInsightContext();
  const { loginData } = useUserContext();

  // State initialization
  const [likesCount, setLikesCount] = useState(0);
  const [isLikedByMe, setIsLikedByMe] = useState(false);
  const [upvotesCount, setUpvotesCount] = useState(0);
  const [isUpvotedByMe, setIsUpvotedByMe] = useState(false);
  const [downvotesCount, setDownvotesCount] = useState(0);
  const [isDownvotedByMe, setIsDownvotedByMe] = useState(false);
  const [sharesCount, setSharesCount] = useState(0);
  const [isSharedByMe, setIsSharedByMe] = useState(false);

  // Get currentUser
  const member = (loginData && loginData.data) || null;
  const currentUserId = member ? member?.userRecordId || "" : getOrCreateGuestId();

  useEffect(() => {
    const allArticleInteractions = (insightsUserInteraction || [])?.filter(
      (i) => i?.insightId === selectedArticle?.id
    );

    // Total likes across all users
    const totalLikes = countReactions(allArticleInteractions, 'isLiked');
    const totalUpvotes = countReactions(allArticleInteractions, 'isUpvoted');
    const totalDownvotes = countReactions(allArticleInteractions, 'isDownvoted');
    const totalShares = countReactions(allArticleInteractions, "isShared");

    // Current user's specific status
    const currentUserInteraction = allArticleInteractions.find(i => i.userId === currentUserId);
    const userHasLiked = currentUserInteraction ? currentUserInteraction.isLiked : false;
    const userHasUpvoted = currentUserInteraction ? currentUserInteraction.isUpvoted : false;
    const userHasDownvoted = currentUserInteraction ? currentUserInteraction.isDownvoted : false;
    const userHasShared = currentUserInteraction ? !!currentUserInteraction.isShared : false;

    setLikesCount(totalLikes);
    setIsLikedByMe(userHasLiked);
    setUpvotesCount(totalUpvotes);
    setIsUpvotedByMe(userHasUpvoted);
    setDownvotesCount(totalDownvotes);
    setIsDownvotedByMe(userHasDownvoted);
    setSharesCount(totalShares);
    setIsSharedByMe(userHasShared);
  }, [insightsUserInteraction, selectedArticle?.id, currentUserId]);

  const handleNormalclose = () => {
    try {
      if (setIsOpen) {
        setIsOpen(false);
      }
    } catch (error) {
      console.log("81 : InteractionComponent : Error handleNormalClose", error);
    }
  }

  // Handle function for Like
  const handleLike = async () => {
    try {
      if (!member) {
        handleNormalclose();
        openModalLogin();
        return;
      }

      const nextLikedState = !isLikedByMe;

      // SetLiked and count
      setIsLikedByMe(nextLikedState);
      setLikesCount((prevCount) => (nextLikedState ? prevCount + 1 : prevCount - 1));

      // payload for the upsert API
      const body = {
        userId: currentUserId || "",
        insightId: selectedArticle?.id || "",
        isLiked: nextLikedState,
      };

      // Call upsert API function
      await upsertInsightsUserInteraction(body);

      // Optional: Trigger your Google Analytics only if it's a "Like" action
      if (nextLikedState) {
        triggerFunction(
          GoogleAnalyticsEnum.BUTTON,
          GoogleAnalyticsEnum.LSA_SALESFORCE_QUICK_INSIGHT_LIKED
        );
      }
    } catch (error) {
      console.error("105 : InteractionComponent : Error in handleLike", error);
    }
  };

  // Handle function for Upvote
  const handleUpvote = async () => {
    try {
      if (!member) {
        handleNormalclose();
        openModalLogin();
        return;
      }

      const upvoteState = !isUpvotedByMe;

      // SetUpvoted and count
      setIsUpvotedByMe(upvoteState);
      setUpvotesCount((prevCount) => (upvoteState ? prevCount + 1 : prevCount - 1));

      // payload for the upsert API
      const body = {
        userId: currentUserId || "",
        insightId: selectedArticle?.id || "",
        isUpvoted: upvoteState,
      };

      // Call upsert API function
      await upsertInsightsUserInteraction(body);

      // Optional: Trigger your Google Analytics only if it's a "Upvote" action
      if (upvoteState) {
        triggerFunction(
          GoogleAnalyticsEnum.BUTTON,
          GoogleAnalyticsEnum.LSA_SALESFORCE_QUICK_INSIGHT_UPVOTED
        );
      }
    } catch (error) {
      console.error("141 : InteractionComponent : Error in handleUpvote", error);
    }
  };

  // Handle function for Downvote
  const handleDownvote = async () => {
    try {
      if (!member) {
        handleNormalclose();
        openModalLogin();
        return;
      }

      const downvoteState = !isDownvotedByMe;

      // SetDownvoted and count
      setIsDownvotedByMe(downvoteState);
      setDownvotesCount((prevCount) => (downvoteState ? prevCount + 1 : prevCount - 1));

      // payload for the upsert API
      const body = {
        userId: currentUserId || "",
        insightId: selectedArticle?.id || "",
        isDownvoted: downvoteState,
      };

      // Call upsert API function
      await upsertInsightsUserInteraction(body);

      // Optional: Trigger your Google Analytics only if it's a "Downvote" action
      if (downvoteState) {
        triggerFunction(
          GoogleAnalyticsEnum.BUTTON,
          GoogleAnalyticsEnum.LSA_SALESFORCE_QUICK_INSIGHT_DOWNVOTED
        );
      }
    } catch (error) {
      console.error("177 : InteractionComponent : Error in handleDownvote", error);
    }
  };

  // Handle function for Share
  const handleShare = async () => {
    // const url = window.location.href;
    const slug = encodeURIComponent(selectedArticle?.title?.toLowerCase()?.replace(/ /g, '-'));
    const shareUrl = `/salesforce-quick-insights/${slug}`;

    let shareSuccess = false;

    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          url: shareUrl,
        });
        shareSuccess = true;

      } catch (error) {
        // User cancelled share
        shareSuccess = false;
        console.log("User closed the share modal without selecting a platform.", error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
      } catch (error) {
        shareSuccess = false;
        console.error("Unable to copy link", error);
      }
    }

    // API Calling for share
    try {
      // set state for count
      if (shareSuccess) {
        setSharesCount((prev) => prev + 1);
      }

      // payload for the upsert API
      const body = {
        userId: currentUserId || "",
        insightId: selectedArticle?.id || "",
        isShared: shareSuccess,
      };

      // Call upsert API function
      await upsertInsightsUserInteraction(body);

      // Optional: Trigger your Google Analytics only if it's a "Share" action
      if (shareSuccess) {
        triggerFunction(
          GoogleAnalyticsEnum.BUTTON,
          GoogleAnalyticsEnum.LSA_SALESFORCE_QUICK_INSIGHT_SHARED
        );
      }
    } catch (error) {
      console.error("236 : InteractionComponent : Error while calling upsert API for share", error);
    }
    onShare?.();
  };

  return (
    <div className="article-actions-wrapper">

      {isInteractionLoading && <Spinner />}

      <div className="article-actions">

        {/* Like */}
        <ToolTip title={"Like"} id={"like"} placement="bottom">
          <button
            type="button"
            className={`action-with-count ${isLikedByMe ? "liked" : ""
              }`}
            onClick={handleLike}
            aria-label="Like"
          >
            {isLikedByMe ? (
              <RiHeartFill />
            ) : (
              <RiHeartLine />
            )}

            <span>{likesCount}</span>
          </button>
        </ToolTip>

        {/* Share */}
        <ToolTip title={"Share"} id={"share"} placement="bottom">
          <button
            type="button"
            className="action-with-count"
            onClick={handleShare}
            aria-label="Share"
          >
            <RiShareForwardLine />
            <span>{sharesCount}</span>
          </button>
        </ToolTip>

        <div className="actions-spacer" />

        {/* Upvote */}
        <ToolTip title={"Upvote"} id={"upvote"} placement="bottom">
          <button
            type="button"
            className={`action-with-count ${isUpvotedByMe ? "active-upvote" : ""
              }`}
            onClick={handleUpvote}
            aria-label="Upvote"
          >
            {isUpvotedByMe ? (
              <RiThumbUpFill />
            ) : (
              <RiThumbUpLine />
            )}

            <span>{upvotesCount}</span>
          </button>
        </ToolTip>

        {/* Downvote */}
        <ToolTip title={"Downvote"} id={"downvote"} placement="bottom">
          <button
            type="button"
            className={`action-with-count ${isDownvotedByMe ? "active-downvote" : ""
              }`}
            onClick={handleDownvote}
            aria-label="Downvote"
          >
            {isDownvotedByMe ? (
              <RiThumbDownFill />
            ) : (
              <RiThumbDownLine />
            )}

            <span>{downvotesCount}</span>
          </button>
        </ToolTip>

      </div>

    </div>
  );
}

export default InteractionComponent;